import { useLayoutEffect, useRef, useState } from 'react';

import { useClickOutside } from '../hooks/use-click-outside';

import type { CSSProperties, ReactNode } from 'react';

interface InfoHintProps {
  /** Rich explanation shown in the popover. Use for content too large for a title tooltip. */
  children: ReactNode;
  /** Accessible label / fallback tooltip on the trigger. */
  label?: string;
  /**
   * Preferred horizontal alignment. The popover auto-flips to the opposite side
   * if the preferred side would overflow the viewport, so the default is fine
   * almost everywhere.
   */
  align?: 'left' | 'right';
}

const POPOVER_WIDTH = 280;

/**
 * Reusable self-documenting hint: a small ⓘ trigger that opens a popover with a
 * richer explanation. Used to make flows (e.g. API-key creation) self-documenting
 * where a one-line `title` tooltip isn't enough. Click-outside + Esc to close.
 *
 * The popover auto-flips left/right based on available viewport space so it never
 * runs off-screen (e.g. hints near a dialog's right edge).
 *
 * For short one-liners prefer a plain `title=` attribute; reach for InfoHint when
 * the explanation needs more than a phrase.
 */
export function InfoHint({ children, label = 'More info', align = 'left' }: InfoHintProps) {
  const [open, setOpen] = useState(false);
  const [resolvedAlign, setResolvedAlign] = useState<'left' | 'right'>(align);
  const ref = useRef<HTMLSpanElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  useClickOutside(ref, () => setOpen(false), open);

  // On open, flip to whichever side keeps the popover within its clipping bounds.
  // The bounds are the nearest scroll/overflow-clipping ancestor (e.g. a dialog
  // with overflow:auto) — NOT the viewport — because that ancestor crops anything
  // past its edge. A left-aligned popover grows rightward from the trigger; flip
  // to right-aligned (grows left) if it would overflow the right bound, and v.v.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    // Walk up to the nearest ancestor that clips overflow; fall back to viewport.
    let leftBound = 0;
    let rightBound = window.innerWidth;
    let el: HTMLElement | null = btnRef.current.parentElement;
    while (el && el !== document.body) {
      const o = getComputedStyle(el).overflow + getComputedStyle(el).overflowX;
      if (/auto|scroll|hidden/.test(o)) {
        const b = el.getBoundingClientRect();
        leftBound = b.left;
        rightBound = b.right;
        break;
      }
      el = el.parentElement;
    }
    const margin = 8;
    const fitsRightward = rect.left + POPOVER_WIDTH + margin <= rightBound;
    const fitsLeftward = rect.right - POPOVER_WIDTH - margin >= leftBound;
    if (align === 'left') {
      setResolvedAlign(fitsRightward || !fitsLeftward ? 'left' : 'right');
    } else {
      setResolvedAlign(fitsLeftward || !fitsRightward ? 'right' : 'left');
    }
  }, [open, align]);

  const popoverStyle: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    [resolvedAlign]: 0,
    zIndex: 50,
    width: POPOVER_WIDTH,
    maxWidth: '70vw',
    padding: '10px 12px',
    background: 'var(--surface-raise, var(--bg-alt))',
    border: '1px solid var(--border-strong, var(--border))',
    borderRadius: 8,
    boxShadow: 'var(--shadow-soft, 0 4px 16px rgba(0,0,0,0.12))',
    fontSize: 12,
    lineHeight: 1.5,
    color: 'var(--text)',
    fontWeight: 400,
    textTransform: 'none',
    letterSpacing: 0,
    whiteSpace: 'normal',
  };

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        title={label}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: '1px solid var(--border-strong, var(--border))',
          background: open ? 'var(--accent)' : 'transparent',
          color: open ? '#fff' : 'var(--text-soft)',
          fontSize: 11,
          lineHeight: 1,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        i
      </button>
      {open && (
        <div role="tooltip" style={popoverStyle}>
          {children}
        </div>
      )}
    </span>
  );
}
